import React, { useMemo } from 'react';
import { Repeat, Calendar } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';

type RecurrenceMode = 'none' | 'weekly' | 'manual';

interface RecurrencePickerProps {
  mode: RecurrenceMode;
  onModeChange: (mode: RecurrenceMode) => void;
  baseDate: string;
  weeklyConfig?: {
    occurrences: number;
  };
  onWeeklyConfigChange?: (config: { occurrences: number }) => void;
  disabled?: boolean;
}

const RecurrencePicker: React.FC<RecurrencePickerProps> = ({
  mode,
  onModeChange,
  baseDate,
  weeklyConfig = { occurrences: 4 },
  onWeeklyConfigChange,
  disabled = false
}) => {
  const weeklyDates = useMemo(() => {
    if (!baseDate || mode !== 'weekly') return [];

    const dates: string[] = [];
    const base = new Date(baseDate);
    const dayOfWeek = base.getDay();

    for (let i = 0; i < weeklyConfig.occurrences; i++) {
      const nextDate = new Date(base);
      nextDate.setDate(base.getDate() + (i * 7));
      dates.push(nextDate.toISOString().split('T')[0]);
    }

    return dates;
  }, [baseDate, mode, weeklyConfig.occurrences]);

  const getDayName = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { weekday: 'long' });
  };

  const formatDate = (dateStr: string) => {
    const date = new Date(dateStr);
    return date.toLocaleDateString('fr-FR', { day: 'numeric', month: 'long', year: 'numeric' });
  };

  return (
    <div className="space-y-4">
      <label className="block text-sm font-medium text-gray-300 mb-2">
        Mode de récurrence
      </label>

      <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
        <button
          onClick={() => !disabled && onModeChange('none')}
          disabled={disabled}
          className={`
            p-4 rounded-lg border-2 transition-all text-left
            ${mode === 'none'
              ? 'bg-blue-600 border-blue-500'
              : 'bg-gray-700 border-gray-600 hover:bg-gray-650'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-blue-400 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-white">Date unique</h4>
              <p className="text-xs text-gray-400 mt-1">Une seule journée</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => !disabled && onModeChange('weekly')}
          disabled={disabled}
          className={`
            p-4 rounded-lg border-2 transition-all text-left
            ${mode === 'weekly'
              ? 'bg-blue-600 border-blue-500'
              : 'bg-gray-700 border-gray-600 hover:bg-gray-650'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className="flex items-center space-x-3">
            <Repeat className="h-5 w-5 text-purple-400 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-white">Récurrence hebdomadaire</h4>
              <p className="text-xs text-gray-400 mt-1">Répéter chaque semaine</p>
            </div>
          </div>
        </button>

        <button
          onClick={() => !disabled && onModeChange('manual')}
          disabled={disabled}
          className={`
            p-4 rounded-lg border-2 transition-all text-left
            ${mode === 'manual'
              ? 'bg-blue-600 border-blue-500'
              : 'bg-gray-700 border-gray-600 hover:bg-gray-650'
            }
            ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
          `}
        >
          <div className="flex items-center space-x-3">
            <Calendar className="h-5 w-5 text-green-400 flex-shrink-0" />
            <div>
              <h4 className="text-sm font-semibold text-white">Sélection manuelle</h4>
              <p className="text-xs text-gray-400 mt-1">Choisir plusieurs dates</p>
            </div>
          </div>
        </button>
      </div>

      <AnimatePresence mode="wait">
        {mode === 'weekly' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-750 rounded-lg border border-gray-600 p-4 space-y-4"
          >
            <div>
              <label className="block text-sm font-medium text-gray-300 mb-2">
                Nombre de semaines
              </label>
              <div className="flex items-center space-x-4">
                {[2, 4, 8, 12].map(num => (
                  <button
                    key={num}
                    onClick={() => onWeeklyConfigChange?.({ occurrences: num })}
                    disabled={disabled}
                    className={`
                      px-4 py-2 rounded-lg text-sm font-medium transition-all
                      ${weeklyConfig.occurrences === num
                        ? 'bg-blue-600 text-white'
                        : 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      }
                      ${disabled ? 'opacity-50 cursor-not-allowed' : ''}
                    `}
                  >
                    {num}
                  </button>
                ))}
              </div>
            </div>

            {baseDate && weeklyDates.length > 0 && (
              <div>
                <div className="flex items-center space-x-2 mb-2">
                  <Repeat className="h-4 w-4 text-purple-400" />
                  <h4 className="text-sm font-semibold text-white">
                    Dates générées ({weeklyDates.length})
                  </h4>
                </div>
                <div className="bg-gray-700 rounded-lg p-3 max-h-40 overflow-y-auto">
                  <div className="space-y-2">
                    {weeklyDates.map((date, index) => (
                      <div
                        key={date}
                        className="flex items-center justify-between text-sm"
                      >
                        <span className="text-gray-300">
                          Semaine {index + 1} - {getDayName(date)}
                        </span>
                        <span className="text-gray-400 text-xs">{formatDate(date)}</span>
                      </div>
                    ))}
                  </div>
                </div>
              </div>
            )}
          </motion.div>
        )}

        {mode === 'manual' && (
          <motion.div
            initial={{ opacity: 0, height: 0 }}
            animate={{ opacity: 1, height: 'auto' }}
            exit={{ opacity: 0, height: 0 }}
            className="bg-gray-750 rounded-lg border border-gray-600 p-4"
          >
            <div className="flex items-center space-x-2 mb-2">
              <Calendar className="h-4 w-4 text-green-400" />
              <p className="text-sm text-gray-300">
                Utilisez le calendrier ci-dessous pour sélectionner plusieurs dates manuellement
              </p>
            </div>
          </motion.div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default RecurrencePicker;
